import { ArrowRight } from 'lucide-react'
import * as React from 'react'

import type { NonFsdsLpCourse } from '@/components/learning-path/non-fsds-learning-path-content'

interface NonFsdsLpCoursePathSectionProps {
  title: string
  subtitle: string
  courses: NonFsdsLpCourse[]
}

export default function NonFsdsLpCoursePathSection({ title, subtitle, courses }: NonFsdsLpCoursePathSectionProps) {
  return (
    <section
      id="course-path"
      data-testid="non-fsds-lp-course-path-section"
      className="relative overflow-hidden bg-gradient-to-b from-white via-blue-50/40 to-white px-4 py-14 sm:px-6 sm:py-16 lg:py-20"
    >
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl md:text-4xl">{title}</h2>
          <p className="mt-3 text-base leading-relaxed text-slate-600 sm:text-lg">{subtitle}</p>
        </div>

        {/* Vertical timeline */}
        <div className="relative mt-12">
          {/* Connector line */}
          <div className="absolute left-5 top-0 hidden h-full w-0.5 bg-gradient-to-b from-blue-300 via-blue-200 to-blue-100 sm:block lg:left-6" />

          <div className="space-y-6 sm:space-y-8">
            {courses.map((course, index) => (
              <div key={course.code} className="relative sm:pl-14 lg:pl-16">
                {/* Timeline dot */}
                <div className="absolute left-0 top-6 hidden sm:flex">
                  <div className="relative z-10 flex size-10 items-center justify-center rounded-full border-[3px] border-blue-400 bg-white text-sm font-bold text-blue-700 shadow-sm lg:size-12 lg:text-base">
                    {index + 1}
                  </div>
                </div>

                {/* Card */}
                <article className="group overflow-hidden rounded-2xl border border-blue-100/80 bg-white shadow-[0_4px_16px_rgba(15,23,42,0.04)] transition-all duration-200 hover:shadow-[0_12px_32px_-12px_rgba(37,99,235,0.2)]">
                  <div className="p-5 sm:p-6 lg:p-7">
                    {/* Top row: mobile step badge + code + duration/level */}
                    <div className="flex flex-wrap items-start gap-3">
                      {/* Mobile step indicator */}
                      <span className="inline-flex size-9 items-center justify-center rounded-full border-2 border-blue-400 bg-white text-sm font-bold text-blue-700 sm:hidden">
                        {index + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-lg bg-blue-100 px-2.5 py-1 text-xs font-bold text-blue-700">
                            {course.code}
                          </span>
                          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                            {course.duration}
                          </span>
                          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
                            {course.level}
                          </span>
                        </div>
                        <h3 className="mt-2 text-xl font-bold text-slate-900 sm:text-2xl">{course.title}</h3>
                      </div>
                    </div>

                    {/* Description bullets */}
                    <div className="mt-4 space-y-2">
                      {(Array.isArray(course.description) ? course.description : []).map((desc, i) => (
                        <div key={i} className="flex items-start">
                          <span className="mr-2.5 mt-2 inline-block size-1.5 flex-shrink-0 rounded-full bg-blue-500" />
                          <p className="text-sm leading-relaxed text-slate-700 sm:text-base">{desc}</p>
                        </div>
                      ))}
                    </div>

                    {/* Skill tags */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      {(Array.isArray(course.skills) ? course.skills : []).map(skill => (
                        <span
                          key={skill}
                          className="rounded-full border border-blue-100 bg-blue-50/80 px-3 py-1 text-xs font-medium text-blue-700"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>

                    {/* CTA */}
                    <div className="mt-5">
                      <a
                        href={course.curriculumUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-blue-700 hover:gap-2.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                      >
                        View details
                        <ArrowRight className="size-4" />
                      </a>
                    </div>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
